import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";

import { ErrorState, Panel, SheetLabel, Skeleton, Status } from "@/components/primitives";
import { api, ApiError } from "@/lib/api";
import { runtimeConfig } from "@/lib/runtime-config";

/**
 * /dev — plan §52.
 *
 * Everything the tools page shows comes from one server call, so config, chain
 * and database are all from the same moment. Four browser calls would give four
 * moments and hide exactly the drift this page exists to catch.
 *
 * The reset button is the only destructive control in the app, and it says what
 * it cannot do as prominently as what it can: the database empties, the vaults
 * do not. Someone who resets and then finds the protocol page unchanged has
 * learned the most important thing this staging environment has to teach.
 */
export function DevPage() {
  const { address } = useAccount();
  const config = runtimeConfig();
  const queryClient = useQueryClient();
  const [resetNote, setResetNote] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["dev-state", address],
    queryFn: () => api.devState(address),
    refetchInterval: 10_000,
  });

  if (!config.devToolsEnabled) {
    return (
      <div className="sheet py-24">
        <ErrorState
          title="Dev tools dimatikan"
          code="DEV_TOOLS_DISABLED"
          action={
            <Link to="/" className="btn btn--ghost">
              Kembali
            </Link>
          }
        >
          Set <code className="datum">DEV_TOOLS_ENABLED=1</code> pada server untuk
          mengaktifkan halaman ini. Ia dimatikan secara default karena berisi
          tombol yang mengosongkan database.
        </ErrorState>
      </div>
    );
  }

  const reset = async () => {
    setResetting(true);
    try {
      const result = await api.devReset();
      setResetNote(result.note);
      await queryClient.invalidateQueries();
    } catch (caught) {
      setResetNote(
        caught instanceof ApiError ? caught.message : "Reset gagal. Lihat log server.",
      );
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="sheet py-16">
      <SheetLabel coordinate="DEV">Test tools</SheetLabel>
      <h1 className="display-section mt-5 text-paper-50">Environment</h1>

      {isError ? (
        <div className="mt-10">
          <ErrorState
            title="Tidak dapat membaca state"
            code="LOAD_FAILED"
            action={
              <button type="button" className="btn btn--ghost" onClick={() => refetch()}>
                Coba lagi
              </button>
            }
          >
            {error instanceof Error ? error.message : "Server tidak menjawab."}
          </ErrorState>
        </div>
      ) : null}

      {/*
        Chain-id agreement first, because it is the failure that makes every
        other number on this page meaningless — every configured address points
        at a different chain's state, and the symptom is unrelated reverts.
      */}
      {data && "chainIdMatches" in data ? (
        <Panel className="mt-10 p-6" marked>
          <SheetLabel>Chain</SheetLabel>
          <div className="mt-4 flex flex-wrap items-center gap-6">
            <Status state={data.chainIdMatches ? "ok" : "bad"}>
              {data.chainIdMatches ? "Chain id cocok" : "Chain id tidak cocok"}
            </Status>
            <p className="datum text-xs">
              config {String(data.chainId)} · node {String(data.observedChainId ?? "—")}
            </p>
            <p className="datum text-xs opacity-60">blok {String(data.blockNumber ?? "—")}</p>
          </div>
        </Panel>
      ) : null}

      {isPending ? (
        <Skeleton className="mt-6 h-96 w-full" />
      ) : (
        <Panel className="mt-6 overflow-hidden">
          <div className="border-b border-ink-700 px-6 py-4">
            <SheetLabel>Raw state</SheetLabel>
          </div>
          {/*
            Printed raw rather than prettified into fields. A tools page whose
            job is diagnosis should show what the server actually said — a
            formatted view is a second place for a field to go missing without
            anyone noticing.
          */}
          <pre className="hexline max-h-[32rem] overflow-auto p-6 leading-relaxed text-paper-300/80">
            {JSON.stringify(data, null, 2)}
          </pre>
        </Panel>
      )}

      <Panel className="mt-8 border-danger/40 p-6">
        <Status state="bad">Destruktif</Status>
        <h2 className="display-section mt-4 text-lg text-paper-50">Reset database</h2>
        <p className="mt-3 max-w-prose text-sm text-paper-300/70">
          Menghapus semua membership, voucher, order dan catatan pembayaran, lalu
          menanam ulang katalog. <strong>Saldo vault on-chain tidak berubah</strong> —
          ASQ yang sudah dikonsumsi tetap terkunci permanen, dan halaman Protocol
          akan tetap menunjukkan angka yang sama setelah reset.
        </p>

        <button
          type="button"
          className="btn btn--ghost mt-6 border-danger text-danger hover:border-danger hover:text-danger"
          onClick={reset}
          disabled={resetting}
        >
          {resetting ? "Mereset…" : "Reset database"}
        </button>

        {resetNote ? (
          <p className="mt-4 text-sm text-paper-300/80">{resetNote}</p>
        ) : null}
      </Panel>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="sheet py-32">
      <SheetLabel coordinate="404">Tidak ditemukan</SheetLabel>
      <h1 className="display-section mt-5 text-paper-50">Halaman ini tidak ada</h1>
      <p className="mt-5 max-w-md text-sm text-paper-300/70">
        Tautan mungkin salah ketik, atau halamannya sudah dipindahkan.
      </p>
      <Link to="/" className="btn btn--primary mt-8">
        Ke beranda
      </Link>
    </div>
  );
}
